import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  stepBasicData,
  stepStreamingUrl,
  stepFrequency,
  stepCabina,
  stepFinalize,
  streamTypes,
  bitrateOptions,
  availableFrequencies,
  cabinaOptions,
  finalResults,
  countryCities,
  successStories,
} from '@/mocks/rfwOnboardingRadios';
import { SwalAlert } from '@/hooks/useSweetAlert';

type OnboardingForm = {
  name: string;
  slogan: string;
  country: string;
  city: string;
  category: string;
  language: string;
  streamUrl: string;
  streamType: string;
  bitrate: string;
  selectedFrequency: string;
  cabinaActive: boolean;
  cabinaModeration: boolean;
  cabinaGreetings: boolean;
  cabinaSongs: boolean;
  cabinaPolls: boolean;
  cabinaContests: boolean;
};

const emptyForm: OnboardingForm = {
  name: '', slogan: '', country: '', city: '', category: '', language: 'Español',
  streamUrl: '', streamType: '', bitrate: '128',
  selectedFrequency: '',
  cabinaActive: true, cabinaModeration: true, cabinaGreetings: true, cabinaSongs: true,
  cabinaPolls: false, cabinaContests: false,
};

export default function OnboardingRadiosPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<OnboardingForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [completed, setCompleted] = useState(false);

  const updateField = (field: keyof OnboardingForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const toggleCabina = (field: keyof OnboardingForm) => {
    setForm((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validateStep1 = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'El nombre de la radio es obligatorio';
    if (!form.country) e.country = 'Seleccioná un país';
    if (!form.city.trim()) e.city = 'La ciudad es obligatoria';
    if (!form.category) e.category = 'Seleccioná una categoría';
    if (!form.language) e.language = 'Seleccioná un idioma';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.streamUrl.trim()) e.streamUrl = 'La URL del stream es obligatoria';
    else if (!/^https?:\/\/.+/.test(form.streamUrl.trim())) e.streamUrl = 'Ingresá una URL válida (http:// o https://)';
    if (!form.streamType) e.streamType = 'Seleccioná el tipo de stream';
    if (!form.bitrate) e.bitrate = 'Seleccioná la calidad de audio';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.selectedFrequency) e.selectedFrequency = 'Elegí una frecuencia para tu radio';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    let valid = true;
    if (step === 1) valid = validateStep1();
    if (step === 2) valid = validateStep2();
    if (step === 3) valid = validateStep3();
    if (!valid) return;

    if (step < 5) {
      setStep((s) => s + 1);
      setConnectionResult('idle');
      setConnectionMessage('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleTestConnection = () => {
    if (!form.streamUrl.trim()) {
      setErrors({ streamUrl: 'Ingresá la URL primero' });
      return;
    }
    if (!/^https?:\/\/.+/.test(form.streamUrl.trim())) {
      setErrors({ streamUrl: 'Ingresá una URL válida' });
      return;
    }

    setIsTestingConnection(true);
    setConnectionResult('idle');
    setConnectionMessage('');

    // Simular test de conexión
    setTimeout(() => {
      const random = Math.random();
      if (random > 0.15) {
        setConnectionResult('success');
        setConnectionMessage('Conexión exitosa — Stream detectado correctamente');
      } else {
        setConnectionResult('error');
        setConnectionMessage('No se pudo conectar al stream. Verificá que la URL sea correcta y que el servidor esté activo.');
      }
      setIsTestingConnection(false);
    }, 1800);
  };

  const handleFinish = () => {
    setCompleted(true);
    SwalAlert.success(
      '¡Tu radio está en RFW!',
      `Radio ${form.name} publicada en la frecuencia ${form.selectedFrequency}. QR generado, perfil creado y club listo.`
    );
  };

  const handleStartOver = () => {
    setForm(emptyForm);
    setStep(1);
    setErrors({});
    setConnectionResult('idle');
    setConnectionMessage('');
    setCompleted(false);
  };

  // ── Pasos ──
  const configs = [stepBasicData, stepStreamingUrl, stepFrequency, stepCabina, stepFinalize];
  const totalTime = configs.reduce((acc, c) => acc + parseInt(c.duration.replace(/[^0-9]/g, '')), 0);

  if (completed) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        {/* Success Screen */}
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-lg w-full text-center">
            {/* Celebration icon */}
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-emerald-500 via-teal-500 to-amber-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/30">
              <i className="ri-check-double-line text-white text-3xl md:text-4xl"></i>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              ¡{form.name} está en RFW!
            </h1>
            <p className="text-foreground-500 text-sm md:text-base mb-8 max-w-sm mx-auto">
              Tu radio ya aparece en el directorio. Compartí tu frecuencia, mostrá tu QR y empezá a crecer.
            </p>

            {/* Frequency badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 border border-amber-200 mb-6">
              <i className="ri-radar-line text-amber-600"></i>
              <span className="text-amber-800 font-bold text-lg font-mono tracking-wider">{form.selectedFrequency}</span>
              <span className="text-amber-600 text-xs">RFW</span>
            </div>

            {/* Results grid */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {finalResults.map((r) => (
                <div key={r.label} className={`bg-background-100 rounded-xl p-4 border border-${r.color}-200 text-left hover:border-${r.color}-300 transition-colors cursor-pointer`}>
                  <div className={`w-10 h-10 rounded-lg bg-${r.color}-100 flex items-center justify-center mb-2`}>
                    <i className={`${r.icon} text-${r.color}-500 text-lg`}></i>
                  </div>
                  <div className="font-semibold text-sm text-foreground-950 mb-0.5">{r.label}</div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{r.description}</p>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="w-full sm:w-auto px-6 py-3 rounded-xl text-sm font-bold bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer transition-all whitespace-nowrap"
              >
                <i className="ri-dashboard-line mr-2"></i>
                Ir al panel de administración
              </button>
              <button
                onClick={() => navigate(`/radio/${form.selectedFrequency.replace('.', '-')}`)}
                className="w-full sm:w-auto px-6 py-3 rounded-xl text-sm font-semibold border border-background-300 text-foreground-700 hover:bg-background-100 cursor-pointer transition-all whitespace-nowrap"
              >
                <i className="ri-eye-line mr-2"></i>
                Ver mi radio
              </button>
            </div>

            <div className="mt-8">
              <button
                onClick={handleStartOver}
                className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer transition-colors"
              >
                Crear otra radio
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100/70 flex flex-col">
      {/* Top bar */}
      <div className="w-full px-4 md:px-6 py-4 flex items-center justify-between bg-background-50 border-b border-background-200">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="hidden sm:inline">RFW</span>
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-foreground-400">
            Paso {step} de 5
          </span>
          <span className="text-xs text-foreground-400 hidden sm:inline">
            <i className="ri-timer-line mr-1"></i>
            ~{Math.round(totalTime / 60)}:{String(totalTime % 60).padStart(2, '0')} total
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-background-200/50 h-1">
        <div
          className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 transition-all duration-500 ease-out"
          style={{ width: `${(step / 5) * 100}%` }}
        ></div>
      </div>

      <div className="flex-1 flex items-start justify-center px-4 pb-16 pt-6">
        <div className="w-full max-w-xl">
          {/* Step indicator pills */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {configs.map((c, i) => (
              <button
                key={c.id}
                onClick={() => i + 1 < step ? setStep(i + 1) : null}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all cursor-pointer ${
                  i + 1 === step
                    ? `bg-gradient-to-br ${c.gradient} text-white shadow-lg scale-110`
                    : i + 1 < step
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-background-200/50 text-foreground-400'
                }`}
              >
                {i + 1 < step ? <i className="ri-check-line"></i> : i + 1}
              </button>
            ))}
          </div>

          {/* Card */}
          <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
            {/* Step header */}
            <div className={`px-6 md:px-8 py-5 border-b border-background-100 bg-gradient-to-r ${configs[step - 1].gradient} text-white`}>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <i className={`${configs[step - 1].icon} text-lg`}></i>
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-white/70 uppercase tracking-wider">
                    Paso {step}
                  </p>
                  <h2 className="text-lg font-bold">
                    {configs[step - 1].title}
                  </h2>
                </div>
                <div className="ml-auto text-xs text-white/70 font-mono">
                  {configs[step - 1].duration}
                </div>
              </div>
              <p className="text-sm text-white/80 pl-[52px]">{configs[step - 1].subtitle}</p>
            </div>

            {/* Step body */}
            <div className="px-6 md:px-8 py-6">
              {/* ── PASO 1: Datos Básicos ── */}
              {step === 1 && (
                <div className="space-y-4">
                  {/* Nombre */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      Nombre de la radio <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="Ej: Radio La Kalle"
                      maxLength={60}
                      className={`w-full px-4 py-2.5 rounded-xl border text-sm text-foreground-950 bg-background-50 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${
                        errors.name ? 'border-rose-300 focus:ring-rose-400' : 'border-background-200'
                      }`}
                    />
                    {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
                  </div>

                  {/* Eslogan */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Eslogan</label>
                    <input
                      type="text"
                      value={form.slogan}
                      onChange={(e) => updateField('slogan', e.target.value)}
                      placeholder="Ej: Donde vive tu música"
                      maxLength={120}
                      className="w-full px-4 py-2.5 rounded-xl border border-background-200 text-sm text-foreground-950 bg-background-50 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all"
                    />
                  </div>

                  {/* País + Ciudad */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                        País <span className="text-rose-500">*</span>
                      </label>
                      <select
                        value={form.country}
                        onChange={(e) => {
                          updateField('country', e.target.value);
                          updateField('city', '');
                        }}
                        className={`w-full px-4 py-2.5 rounded-xl border text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all cursor-pointer ${
                          errors.country ? 'border-rose-300 focus:ring-rose-400' : 'border-background-200'
                        }`}
                      >
                        <option value="">Seleccionar país</option>
                        {stepBasicData.fields.find((f) => f.name === 'country')?.options?.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      {errors.country && <p className="text-xs text-rose-500 mt-1">{errors.country}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                        Ciudad <span className="text-rose-500">*</span>
                      </label>
                      {form.country && countryCities[form.country] ? (
                        <select
                          value={form.city}
                          onChange={(e) => updateField('city', e.target.value)}
                          className={`w-full px-4 py-2.5 rounded-xl border text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all cursor-pointer ${
                            errors.city ? 'border-rose-300 focus:ring-rose-400' : 'border-background-200'
                          }`}
                        >
                          <option value="">Seleccionar ciudad</option>
                          {countryCities[form.country].map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                          <option value="__other__">Otra ciudad...</option>
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={form.city}
                          onChange={(e) => updateField('city', e.target.value)}
                          placeholder="Ej: Buenos Aires"
                          maxLength={80}
                          className={`w-full px-4 py-2.5 rounded-xl border text-sm text-foreground-950 bg-background-50 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${
                            errors.city ? 'border-rose-300 focus:ring-rose-400' : 'border-background-200'
                          }`}
                        />
                      )}
                      {errors.city && <p className="text-xs text-rose-500 mt-1">{errors.city}</p>}
                    </div>
                  </div>

                  {/* Categoría + Idioma */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                        Categoría <span className="text-rose-500">*</span>
                      </label>
                      <select
                        value={form.category}
                        onChange={(e) => updateField('category', e.target.value)}
                        className={`w-full px-4 py-2.5 rounded-xl border text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all cursor-pointer ${
                          errors.category ? 'border-rose-300 focus:ring-rose-400' : 'border-background-200'
                        }`}
                      >
                        <option value="">Seleccionar categoría</option>
                        {stepBasicData.fields.find((f) => f.name === 'category')?.options?.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      {errors.category && <p className="text-xs text-rose-500 mt-1">{errors.category}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                        Idioma <span className="text-rose-500">*</span>
                      </label>
                      <select
                        value={form.language}
                        onChange={(e) => updateField('language', e.target.value)}
                        className="w-full px-4 py-2.5 rounded-xl border border-background-200 text-sm text-foreground-950 bg-background-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all cursor-pointer"
                      >
                        {stepBasicData.fields.find((f) => f.name === 'language')?.options?.map((l) => (
                          <option key={l} value={l}>{l}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* ── PASO 2: Streaming URL ── */}
              {step === 2 && (
                <div className="space-y-5">
                  {/* Stream URL */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                      URL del stream <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={form.streamUrl}
                        onChange={(e) => {
                          updateField('streamUrl', e.target.value);
                          setConnectionResult('idle');
                          setConnectionMessage('');
                        }}
                        placeholder="https://miradio.com/stream"
                        className={`flex-1 px-4 py-2.5 rounded-xl border text-sm text-foreground-950 bg-background-50 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-sky-400 transition-all ${
                          errors.streamUrl ? 'border-rose-300 focus:ring-rose-400' : 'border-background-200'
                        }`}
                      />
                      <button
                        onClick={handleTestConnection}
                        disabled={isTestingConnection}
                        className={`px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all cursor-pointer ${
                          isTestingConnection
                            ? 'bg-background-200 text-foreground-400 cursor-wait'
                            : 'bg-sky-500 hover:bg-sky-600 text-white'
                        }`}
                      >
                        {isTestingConnection ? (
                          <span className="flex items-center gap-1.5">
                            <i className="ri-loader-4-line animate-spin"></i>
                            Probando...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5">
                            <i className="ri-plug-line"></i>
                            Probar
                          </span>
                        )}
                      </button>
                    </div>
                    {errors.streamUrl && <p className="text-xs text-rose-500 mt-1">{errors.streamUrl}</p>}

                    {/* Connection result */}
                    {connectionResult === 'success' && (
                      <div className="mt-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                        <i className="ri-checkbox-circle-fill text-emerald-500"></i>
                        <span className="text-xs text-emerald-700">{connectionMessage}</span>
                      </div>
                    )}
                    {connectionResult === 'error' && (
                      <div className="mt-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 flex items-center gap-2">
                        <i className="ri-close-circle-fill text-rose-500"></i>
                        <span className="text-xs text-rose-700">{connectionMessage}</span>
                      </div>
                    )}
                  </div>

                  {/* Stream type */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2">
                      Tipo de stream <span className="text-rose-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {streamTypes.map((st) => (
                        <button
                          key={st.id}
                          onClick={() => updateField('streamType', st.id)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer ${
                            form.streamType === st.id
                              ? `border-${st.color}-400 bg-${st.color}-50 ring-2 ring-${st.color}-200`
                              : 'border-background-200 hover:border-background-300'
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-lg bg-${st.color}-100 flex items-center justify-center`}>
                            <i className={`${st.icon} text-${st.color}-500 text-base`}></i>
                          </div>
                          <div>
                            <div className="font-semibold text-sm text-foreground-950">{st.label}</div>
                            <div className="text-[10px] text-foreground-400">{st.description}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                    {errors.streamType && <p className="text-xs text-rose-500 mt-1">{errors.streamType}</p>}
                  </div>

                  {/* Bitrate */}
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-2">
                      Calidad de audio <span className="text-rose-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {bitrateOptions.map((b) => (
                        <button
                          key={b.value}
                          onClick={() => updateField('bitrate', b.value)}
                          className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all cursor-pointer ${
                            form.bitrate === b.value
                              ? 'bg-sky-500 text-white'
                              : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                          }`}
                        >
                          {b.label}
                          <span className={`ml-1 ${form.bitrate === b.value ? 'text-white/60' : 'text-foreground-400'}`}>
                            {b.quality}
                          </span>
                        </button>
                      ))}
                    </div>
                    {errors.bitrate && <p className="text-xs text-rose-500 mt-1">{errors.bitrate}</p>}
                  </div>
                </div>
              )}

              {/* ── PASO 3: Frecuencia RFW ── */}
              {step === 3 && (
                <div className="space-y-4">
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    Elegí la frecuencia que va a identificar a tu radio en RFW. Es como el número de dial, pero en la web.
                  </p>

                  {/* Type filter */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-foreground-400 self-center mr-1">Filtrar:</span>
                    {['Todas', 'Recomendadas', 'Premium', 'Estándar'].map((filter) => (
                      <span key={filter} className={`px-2.5 py-1 rounded-full text-[10px] font-medium ${
                        filter === 'Recomendadas' ? 'bg-amber-100 text-amber-700' :
                        filter === 'Premium' ? 'bg-violet-100 text-violet-700' :
                        filter === 'Estándar' ? 'bg-background-200/70 text-foreground-500' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {filter}
                      </span>
                    ))}
                  </div>

                  {/* Frequency grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {availableFrequencies.map((freq) => (
                      <button
                        key={freq.frequency}
                        onClick={() => updateField('selectedFrequency', freq.frequency)}
                        className={`relative p-4 rounded-xl border text-center transition-all cursor-pointer ${
                          form.selectedFrequency === freq.frequency
                            ? freq.type === 'premium'
                              ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-200'
                              : freq.type === 'recommended'
                                ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200'
                                : 'border-emerald-400 bg-emerald-50 ring-2 ring-emerald-200'
                            : 'border-background-200 hover:border-background-300'
                        }`}
                      >
                        {freq.badge && (
                          <span className={`absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-bold whitespace-nowrap ${
                            freq.type === 'premium' ? 'bg-violet-500 text-white' :
                            freq.type === 'recommended' ? 'bg-amber-500 text-white' : ''
                          }`}>
                            {freq.badge}
                          </span>
                        )}
                        <div className={`text-xl font-bold font-mono mb-1 ${
                          freq.type === 'premium' ? 'text-violet-700' :
                          freq.type === 'recommended' ? 'text-amber-700' :
                          'text-foreground-800'
                        }`}>
                          {freq.frequency}
                        </div>
                        <div className="text-[10px] text-foreground-400">RFW</div>
                        <div className="text-[10px] text-foreground-500 mt-1">{freq.label}</div>
                        {freq.price && (
                          <div className="text-[9px] text-violet-500 font-semibold mt-1">{freq.price}</div>
                        )}
                        {form.selectedFrequency === freq.frequency && (
                          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                            <i className="ri-check-line text-white text-[10px]"></i>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  {errors.selectedFrequency && <p className="text-xs text-rose-500 mt-1">{errors.selectedFrequency}</p>}

                  <div className="text-[10px] text-foreground-400 italic text-center">
                    {stepFrequency.tip}
                  </div>
                </div>
              )}

              {/* ── PASO 4: Primera Cabina ── */}
              {step === 4 && (
                <div className="space-y-3">
                  <p className="text-xs text-foreground-500 leading-relaxed mb-2">
                    Tu cabina ya viene configurada con lo esencial. Ajustá lo que quieras — podés cambiar todo después.
                  </p>

                  {cabinaOptions.map((opt) => {
                    const fieldName = `cabina${opt.id.charAt(0).toUpperCase() + opt.id.slice(1)}` as keyof OnboardingForm;
                    const isEnabled = form[fieldName] === true;

                    return (
                      <div
                        key={opt.id}
                        className={`flex items-center gap-4 p-4 rounded-xl border transition-all cursor-pointer ${
                          isEnabled ? 'border-emerald-200 bg-emerald-50/50' : 'border-background-200 bg-background-50'
                        }`}
                        onClick={() => toggleCabina(fieldName)}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isEnabled ? 'bg-emerald-100' : 'bg-background-200/70'
                        }`}>
                          <i className={`${opt.icon} text-lg ${isEnabled ? 'text-emerald-600' : 'text-foreground-400'}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="font-semibold text-sm text-foreground-950">{opt.label}</span>
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                              opt.category === 'core' ? 'bg-violet-100 text-violet-600' :
                              opt.category === 'social' ? 'bg-amber-100 text-amber-600' :
                              'bg-rose-100 text-rose-600'
                            }`}>
                              {opt.category === 'core' ? 'Esencial' : opt.category === 'social' ? 'Social' : 'Engagement'}
                            </span>
                          </div>
                          <p className="text-xs text-foreground-500 leading-relaxed">{opt.description}</p>
                        </div>
                        <div className={`w-10 h-6 rounded-full transition-colors flex items-center ${
                          isEnabled ? 'bg-emerald-500' : 'bg-background-300'
                        }`}>
                          <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                            isEnabled ? 'translate-x-[22px]' : 'translate-x-[2px]'
                          }`}></div>
                        </div>
                      </div>
                    );
                  })}

                  <div className="text-[10px] text-foreground-400 italic text-center mt-3">
                    {stepCabina.tip}
                  </div>
                </div>
              )}

              {/* ── PASO 5: Finalizar ── */}
              {step === 5 && (
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200 text-center mb-2">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-rocket-line text-white text-xl"></i>
                    </div>
                    <h3 className="text-emerald-800 font-bold text-base mb-1">¡Tu radio está lista!</h3>
                    <p className="text-emerald-600 text-xs leading-relaxed">
                      Completaste los 4 pasos en tiempo récord. Ahora solo queda publicar.
                    </p>
                  </div>

                  {/* Summary */}
                  <div className="bg-background-100 rounded-xl p-4 border border-background-200">
                    <div className="text-xs font-semibold text-foreground-700 mb-3">Resumen de tu radio</div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Nombre</span>
                        <span className="text-xs font-semibold text-foreground-950">{form.name || '—'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Ubicación</span>
                        <span className="text-xs font-semibold text-foreground-950">
                          {form.city && form.country ? `${form.city}, ${form.country}` : '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Categoría</span>
                        <span className="text-xs font-semibold text-foreground-950">{form.category || '—'}</span>
                      </div>
                      <div className="h-px bg-background-200"></div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Stream</span>
                        <span className="text-xs font-semibold text-foreground-950 flex items-center gap-1">
                          {connectionResult === 'success' ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                              Conectado
                            </>
                          ) : connectionResult === 'error' ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                              Sin verificar
                            </>
                          ) : (
                            <span className="text-foreground-400">No probado</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Tipo</span>
                        <span className="text-xs font-semibold text-foreground-950">
                          {streamTypes.find((s) => s.id === form.streamType)?.label || '—'}
                        </span>
                      </div>
                      <div className="h-px bg-background-200"></div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Frecuencia</span>
                        <span className="text-xs font-bold font-mono text-amber-700">
                          {form.selectedFrequency || '—'} RFW
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground-500">Cabina</span>
                        <span className="text-xs font-semibold text-foreground-950">
                          {[
                            form.cabinaActive && 'Activa',
                            form.cabinaModeration && 'Moderación',
                            form.cabinaGreetings && 'Saludos',
                            form.cabinaSongs && 'Canciones',
                          ].filter(Boolean).join(', ') || 'Desactivada'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* What you'll get */}
                  <div className="grid grid-cols-2 gap-2">
                    {finalResults.map((r) => (
                      <div key={r.label} className="flex items-center gap-2 bg-background-100 rounded-lg p-3 border border-background-200/70">
                        <i className={`${r.icon} text-${r.color}-500 text-base`}></i>
                        <span className="text-xs font-medium text-foreground-700">{r.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Success stories */}
                  <div className="bg-background-100 rounded-xl p-4 border border-background-200/70">
                    <div className="text-xs font-semibold text-foreground-700 mb-2">Lo hicieron en menos de 4 minutos</div>
                    {successStories.slice(0, 1).map((story, i) => (
                      <p key={i} className="text-xs text-foreground-500 italic">
                        "{story.name} ({story.frequency}, {story.city}) — {story.testimonial}"
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Step footer */}
            <div className="px-6 md:px-8 py-4 border-t border-background-200 flex items-center justify-between bg-background-100/70">
              <div>
                {step > 1 && (
                  <button
                    onClick={handleBack}
                    className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line mr-1.5"></i>
                    Anterior
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {step === 5 ? (
                  <button
                    onClick={handleFinish}
                    className="px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 hover:from-emerald-600 hover:via-teal-600 hover:to-amber-600 text-white cursor-pointer transition-all whitespace-nowrap shadow-lg shadow-emerald-500/20"
                  >
                    <i className="ri-rocket-line mr-2"></i>
                    Publicar mi radio
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white cursor-pointer transition-all whitespace-nowrap"
                  >
                    Siguiente
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Skip hint */}
          {step === 4 && (
            <p className="text-center mt-4 text-[11px] text-foreground-400">
              ¿Querés configurar después?{' '}
              <button
                onClick={() => setStep(5)}
                className="text-emerald-600 hover:text-emerald-700 font-semibold cursor-pointer transition-colors"
              >
                Saltar al paso final
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}